// Three.js scene rendering directly from a GameSnapshot.
//
// Reads `snapshot.scene.children`, partitions into tiles + cities by `type`,
// hands off to dedicated renderers. Camera is overhead-orbital with damping;
// the player owns it, except for one-shot go-to flights (camera/) and
// milestone cinematics (../cinematics/).

import type { GameEvent, GameSnapshot, SceneNode, SeaState } from '@aod/protocol';
import { RIM_SEA } from '@aod/protocol';
import { OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { ActionMode } from '../action/ActionMode.js';
import { CinematicCameraRig, type OrbitControlsLike } from '../cinematics/CinematicCameraRig.js';
import { useSettings } from '../settings/useSettings.js';
import { tickToGameTime } from '../sim/gameTime.js';
import type { Archetype } from '../ui/DoctrinePanel/index.js';
import { Birds, DistantClouds } from './Ambient.js';
import { AmbientParticles } from './AmbientParticles.js';
import { AtmosphericClouds } from './AtmosphericClouds.js';
import { BoardScaleRefit } from './BoardScaleRefit.js';
import { BridgeTargetRings, isEngineer } from './BridgeTargetRings.js';
import { CoastalDressing } from './CoastalDressing.js';
import { ShadowSpots, buildCityShadowSpots, buildUnitShadowSpots } from './ContactShadows.js';
import { DayNightLighting } from './DayNightLighting.js';
import { OpenSea, SkyDome } from './Environment.js';
import { GroundFog } from './GroundFog.js';
import { HeroPennants } from './HeroPennants.js';
import { HEX_HEIGHT, HexGrid, axialToWorld } from './HexGrid.js';
import { Infrastructure } from './Infrastructure.js';
import { MountainSnow, type SnowPeak } from './MountainSnow.js';
import { MoveTargetRings } from './MoveTargetRings.js';
import { NightLights } from './NightLights.js';
import { PostProcessing } from './PostProcessing.js';
import { RainShower } from './RainShower.js';
import { RenderStatsCollector } from './RenderStatsCollector.js';
import { SelectionRing } from './SelectionRing.js';
import { Settlements } from './Settlements.js';
import { TerritoryOverlay } from './TerritoryOverlay.js';
import { TileDressing } from './TileDressing.js';
import { TileImprovements } from './TileImprovements.js';
import { VfxLayer } from './VfxLayer.js';
import { VoxelCities } from './VoxelCities.js';
import { VoxelUnits } from './VoxelUnits.js';
import { DOUBLE_SCALE, getBoardScale, scalePoint, setBoardScale } from './boardScale.js';
import { CameraFlight } from './camera/CameraFlight.js';
import { CameraPanClamp } from './camera/CameraPanClamp.js';
import { CameraPanInput } from './camera/CameraPanInput.js';
import { CameraViewPublisher } from './camera/CameraViewPublisher.js';
import type { CameraFocusRequest, FlightControls } from './camera/flight.js';
import { findCapitalFocus } from './capitalFocus.js';
import {
  buildBiomeMap,
  mountainClusterBoost,
  terrainLevelsActive,
  tileElevationAt,
  tileSurfaceY,
} from './elevation.js';
import { FaunaLayer } from './fauna/FaunaLayer.js';
import { isSinglePlayerSnapshot } from './isSinglePlayerSnapshot.js';
import {
  CAMERA_FOV,
  FOG_COLOR,
  FOG_DENSITY,
  SKY_HORIZON,
  SKY_ZENITH,
  getCameraPosition,
} from './sceneRenderConfig.js';
import { SeaFrameLayer } from './sea/SeaFrameLayer.js';
import { extraOceanRings, frameForMode } from './sea/seaFrame.js';
import { protoSeaRings, seaProto } from './sea/seaProto.js';
import { applyLandlockedWall } from './sea/seaTiles.js';
import { climateAdjustedDay } from './seasons.js';
import type { EngagementVisualState } from './useCombatEngagements.js';

interface SceneProps {
  readonly snapshot: GameSnapshot;
  readonly events?: ReadonlyArray<GameEvent | { type: string; payload?: Record<string, unknown> }>;
  readonly localPlayerIndex?: number;
  readonly onTileHover?: (tile: SceneNode | null) => void;
  readonly onUnitHover?: (unit: SceneNode | null) => void;
  readonly onTileClick?: (tile: SceneNode) => void;
  readonly onCityClick?: (city: SceneNode) => void;
  readonly onUnitClick?: (unit: SceneNode) => void;
  readonly onMoveClick?: (q: number, r: number) => void;
  readonly onBackgroundClick?: () => void;
  readonly selectedTileId?: string | null;
  readonly selectedCityId?: string | null;
  readonly selectedUnitId?: string | null;
  /**
   * Glide the camera to look at a point (minimap, "Go to", selection). A new
   * `seq` flies once, then the player has the camera again; see camera/flight.ts.
   */
  readonly focusRequest?: CameraFocusRequest | null;
  /** Local player's dominant doctrine — drives a subtle color grade. */
  readonly doctrine?: Archetype | null;
  /** Combat engagement visual state — drives 3D jitter + step-back on units. */
  readonly engagements?: ReadonlyMap<string, EngagementVisualState>;
  /** Current sim tick — used to gate per-unit jitter windows. */
  readonly currentTick?: number;
  /** Map climate (terrain-levels D11) — pins the season for arctic/tropical. */
  readonly climate?: string;
  /** The sea axis as the sim resolved it (sea-design §3 A2) — the mode and
   * the frame the board was generated with. */
  readonly sea?: SeaState;
  /** Engineer bridge build (ring click on a river-edge neighbour). */
  readonly onBuildBridge?: (unitId: string, q: number, r: number) => void;
}

export function Scene({
  snapshot,
  events = [],
  localPlayerIndex = 0,
  onTileHover,
  onUnitHover,
  onTileClick,
  onCityClick,
  onUnitClick,
  onMoveClick,
  onBackgroundClick,
  selectedTileId,
  selectedCityId,
  selectedUnitId,
  focusRequest = null,
  doctrine = null,
  engagements,
  currentTick = 0,
  climate,
  sea,
  onBuildBridge,
}: SceneProps) {
  const {
    tiles: simTiles,
    cities,
    units,
  } = useMemo(() => {
    const all = snapshot.scene.children ?? [];
    const tiles: SceneNode[] = [];
    const cities: SceneNode[] = [];
    const units: SceneNode[] = [];
    for (const node of all) {
      if (node.type === 'tile') tiles.push(node);
      else if (node.type === 'city') cities.push(node);
      else if (node.type === 'unit') units.push(node);
    }
    return { tiles, cities, units };
  }, [snapshot]);
  // The sea axis the sim generated the board with (sea-design §3 A2). The
  // snapshot publishes it; a board without it is today's rim board.
  const seaState = sea ?? RIM_SEA;
  // DEV preview of the sea frame (?seaProto=…, default OFF => `tiles` IS
  // `simTiles`). A real landlocked match already has its wall from the sim,
  // so the preview stands down when the sim says the board is not a rim.
  const proto = seaProto();
  const preview = proto && seaState.mode === 'rim' ? proto : null;
  const tiles = useMemo(
    () =>
      preview?.mode === 'landlocked' ? (applyLandlockedWall(simTiles) as SceneNode[]) : simTiles,
    [simTiles, preview],
  );
  // The sea layer. A landlocked board gets the mist curtain (the void
  // beyond the wall is fog, not sea); an open board's ocean is real tiles,
  // so it needs no layer; the rim board has none outside the DEV preview.
  const landlocked = seaState.mode === 'landlocked';
  const seaLayer = useMemo(() => {
    if (landlocked) return { mode: seaState.mode, frame: seaState.frame };
    if (preview) {
      return { mode: preview.mode, frame: frameForMode(preview.mode, preview.width) };
    }
    return null;
  }, [landlocked, seaState.mode, seaState.frame, preview]);
  // How far past the board the camera may reach: the open sea's extra
  // ocean rings, or a ring of slack for the landlocked mist curtain.
  const seaRings = preview
    ? protoSeaRings(preview)
    : landlocked
      ? 1
      : extraOceanRings(seaState.frame);

  const resourceColors = useMemo<Record<string, string>>(() => {
    const globals = snapshot.state.globals as
      | { resources?: ReadonlyArray<{ id: string; color: string }> }
      | undefined;
    const map: Record<string, string> = {};
    for (const r of globals?.resources ?? []) {
      map[r.id] = r.color;
    }
    return map;
  }, [snapshot]);

  const biomeMap = useMemo(() => buildBiomeMap(tiles), [tiles]);
  // City hexes, as "q,r" keys. Keyed on the joined string so the Set (and the
  // tree scatter that depends on it) only changes when a city appears or goes.
  const settlementKey = cities
    .map(
      (c) =>
        `${(c.props as Record<string, unknown> | undefined)?.q},${(c.props as Record<string, unknown> | undefined)?.r}`,
    )
    .join('|');
  const settlementCoords = useMemo(
    () => new Set(settlementKey ? settlementKey.split('|') : []),
    [settlementKey],
  );

  // Fog of war: visible tile coords for the local player. Skipped entirely
  // in single-player so the map is fully visible from frame one.
  // (Audit 2026-07-16 visual finding.)
  const visibleCoords = useMemo(() => {
    if (isSinglePlayerSnapshot(snapshot)) return null;
    const globals = snapshot.state.globals as { visibility?: Record<string, string[]> } | undefined;
    const raw = globals?.visibility?.[String(localPlayerIndex)];
    if (!raw || raw.length === 0) return null; // null = no fog (show all)
    return new Set<string>(raw);
  }, [snapshot.state.globals, localPlayerIndex, snapshot]);

  // Accumulate all ever-visible coords for fog-of-war depth (explored vs unexplored).
  const exploredCoords = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (visibleCoords) {
      const explored = exploredCoords.current;
      for (const coord of visibleCoords) {
        explored.add(coord);
      }
    }
  }, [visibleCoords]);

  const selectedTileData = useMemo(() => {
    if (!selectedTileId) return null;
    const t = tiles.find((tile) => tile.id === selectedTileId);
    if (!t) return null;
    const props = t.props as Record<string, unknown> | undefined;
    const q = (props?.q as number) ?? 0;
    const r = (props?.r as number) ?? 0;
    return { position: t.transform.position, q, r };
  }, [tiles, selectedTileId]);

  const selectedUnitNode = useMemo(() => {
    if (!selectedUnitId) return null;
    return units.find((u) => u.id === selectedUnitId) ?? null;
  }, [units, selectedUnitId]);

  // Center camera on the centroid of the map for a good default framing.
  const center = useMemo<[number, number, number]>(() => {
    if (tiles.length === 0) return [0, 0, 0];
    let sx = 0;
    let sz = 0;
    for (const t of tiles) {
      sx += t.transform.position[0];
      sz += t.transform.position[2];
    }
    return [sx / tiles.length, 0, sz / tiles.length];
  }, [tiles]);

  // Bias the *initial* camera frame toward the local player's starting
  // capital. Derived once on first render via a ref so subsequent
  // re-renders don't drag the camera off whatever the user has panned
  // to via minimap/unit selection.
  const initialFocusRef = useRef<[number, number, number] | null>(null);
  if (initialFocusRef.current === null) {
    initialFocusRef.current = findCapitalFocus(snapshot, localPlayerIndex) ?? center;
  }
  const initialCameraCenter = initialFocusRef.current;
  // Milestone cinematics (CinematicCameraRig) and go-to flights
  // (CameraFlight) both drive the camera through these controls.
  const controlsRef = useRef<(OrbitControlsLike & FlightControls) | null>(null);

  // Compute particle spawn bounds from tile extents.
  const particleBounds = useMemo<[number, number, number, number] | null>(() => {
    if (tiles.length === 0) return null;
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minZ = Number.POSITIVE_INFINITY;
    let maxZ = Number.NEGATIVE_INFINITY;
    for (const t of tiles) {
      const x = t.transform.position[0];
      const z = t.transform.position[2];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    const pad = 1;
    return [minX - pad, maxX + pad, minZ - pad, maxZ + pad];
  }, [tiles]);

  // Season tint input: the in-game day (1..365). Day-granular, so seasonal
  // colour work runs once per in-game day (~1s at 1× speed), not per tick.
  const seasonDay = useMemo(
    () => climateAdjustedDay(climate, tickToGameTime(currentTick).day),
    [climate, currentTick],
  );

  // Massif-core peaks (mountain tiles boosted by 3+ mountain neighbours):
  // MountainSnow flurries fall over these.
  const snowPeaks = useMemo<SnowPeak[]>(() => {
    const out: SnowPeak[] = [];
    for (const tile of tiles) {
      const props = tile.props as Record<string, unknown> | undefined;
      const q = props?.q as number | undefined;
      const r = props?.r as number | undefined;
      if (q === undefined || r === undefined) continue;
      if (props?.biome !== 'mountain') continue;
      // Levelled board: snow-line peaks are the high ground (level >= 9);
      // otherwise the massif-core heuristic.
      if (terrainLevelsActive()) {
        if (((props?.level as number | undefined) ?? 0) < 9) continue;
      } else if (mountainClusterBoost(q, r, biomeMap) <= 0) continue;
      const [x, , z] = tile.transform.position;
      out.push({ x, y: tileSurfaceY(q, r, biomeMap), z });
    }
    return out;
  }, [tiles, biomeMap]);

  // Large Board: world content renders at 2x inside a scaled <group>; the
  // camera, sky and post stack stay outside. Synced during render (not in an
  // effect) so mount-time closures — the Canvas camera prop and the
  // onCreated audit closures — read the correct live value.
  const doubleBoard = useSettings((st) => st.doubleBoard);
  const showTerritory = useSettings((st) => st.showTerritory);
  const showDayNight = useSettings((st) => st.showDayNight);
  const particleEffects = useSettings((st) => st.particleEffects);
  const showClouds = useSettings((st) => st.showClouds);
  const boardScale = doubleBoard ? DOUBLE_SCALE : 1;
  setBoardScale(boardScale);
  const scaledCenter = useMemo(() => scalePoint(center), [center, boardScale]);

  // Contact shadows: one blob per unit / city, rebuilt with each snapshot.
  const unitShadows = useMemo(() => buildUnitShadowSpots(units, biomeMap), [units, biomeMap]);
  const cityShadows = useMemo(() => buildCityShadowSpots(cities, biomeMap), [cities, biomeMap]);

  return (
    <Canvas
      data-testid="game-canvas"
      shadows
      camera={{ position: scalePoint(getCameraPosition(initialCameraCenter)), fov: CAMERA_FOV }}
      style={{
        position: 'absolute',
        inset: 0,
        // Fallback until the sky dome draws (matches its zenith → horizon).
        background: `linear-gradient(to bottom, ${SKY_ZENITH} 0%, ${SKY_HORIZON} 100%)`,
      }}
      onCreated={({ camera, gl, scene }) => {
        // Screen audits read the live scene: screen-audit's dual-dom plugin
        // and scripts/audit/ui-invariants.mjs (props buried in terrain, grid
        // lines off the tile edges). Meshes they inspect carry `name`s.
        const w = window as unknown as {
          __GAME_SCENE__?: THREE.Scene;
          __GAME_CAMERA__?: THREE.Camera;
          THREE?: typeof THREE;
        };
        w.__GAME_SCENE__ = scene;
        w.__GAME_CAMERA__ = camera;
        w.THREE = THREE;
        const project = (q: number, r: number, yOffset = HEX_HEIGHT / 2) => {
          const [wx, , wz] = scalePoint(axialToWorld(q, r));
          const worldPos = new THREE.Vector3(wx, yOffset * getBoardScale(), wz);
          const screenPos = worldPos.clone().project(camera);
          const rect = gl.domElement.getBoundingClientRect();
          return {
            x: rect.left + rect.width * (screenPos.x * 0.5 + 0.5),
            y: rect.top + rect.height * (-screenPos.y * 0.5 + 0.5),
          };
        };
        (
          window as unknown as {
            __aodCamera?: {
              project: typeof project;
              world: typeof axialToWorld;
              target?: () => [number, number, number] | null;
            };
          }
        ).__aodCamera = {
          project,
          world: (q: number, r: number) => scalePoint(axialToWorld(q, r)),
          // Live orbit target (rendered space) for the minimap-drag e2e.
          target: () => {
            const t = controlsRef.current?.target;
            return t ? [t.x, t.y, t.z] : null;
          },
        };
      }}
      onPointerMissed={() => {
        onBackgroundClick?.();
      }}
    >
      <fogExp2 args={[FOG_COLOR, FOG_DENSITY]} attach="fog" />
      <RenderStatsCollector />
      <BoardScaleRefit scale={boardScale} initialTarget={scaledCenter} controls={controlsRef} />
      <SkyDome />
      <OpenSea color={landlocked ? FOG_COLOR : undefined} />
      <DistantClouds center={scaledCenter} />
      <Birds center={scaledCenter} />
      <DayNightLighting enabled={showDayNight} />
      <group scale={boardScale}>
        <HexGrid
          tiles={tiles}
          resourceColors={resourceColors}
          visibleCoords={visibleCoords}
          exploredCoords={exploredCoords.current}
          onPointerOver={onTileHover}
          onClick={onTileClick}
          settlementCoords={settlementCoords}
          seasonDay={seasonDay}
          climate={climate}
          detail={doubleBoard ? 2 : 1}
        />
        {seaLayer && <SeaFrameLayer tiles={tiles} mode={seaLayer.mode} frame={seaLayer.frame} />}
        <Infrastructure tiles={tiles} />
        <TileImprovements tiles={tiles} seasonDay={seasonDay} />
        <ShadowSpots spots={cityShadows} />
        <NightLights cities={cityShadows} units={unitShadows} />
        <MountainSnow peaks={snowPeaks} />
        {particleEffects && <RainShower tiles={tiles} />}
        <GroundFog
          tiles={tiles}
          biomeMap={biomeMap}
          visibleCoords={visibleCoords}
          currentTick={currentTick}
        />
        <VoxelCities
          cities={cities}
          onClick={onCityClick}
          selectedId={selectedCityId}
          biomeMap={biomeMap}
        />
        <Settlements cities={cities} biomeMap={biomeMap} onClick={onCityClick} />
        <VoxelUnits
          units={units}
          onClick={onUnitClick}
          onUnitHover={onUnitHover}
          selectedId={selectedUnitId}
          visibleCoords={visibleCoords}
          exploredCoords={exploredCoords.current}
          biomeMap={biomeMap}
          engagements={engagements}
          currentTick={currentTick}
        />
        <HeroPennants units={units} cities={cities} visibleCoords={visibleCoords} />
        <TileDressing tiles={tiles} visibleCoords={visibleCoords} />
        <CoastalDressing tiles={tiles} visibleCoords={visibleCoords} />
        <FaunaLayer tiles={tiles} biomeMap={biomeMap} visibleCoords={visibleCoords} />
        <ShadowSpots spots={unitShadows} />
        <VfxLayer events={events} />
        {selectedUnitNode && (
          <MoveTargetRings
            unit={selectedUnitNode}
            tiles={tiles}
            cities={cities}
            units={units}
            onMoveClick={onMoveClick}
          />
        )}
        {selectedUnitNode && isEngineer(selectedUnitNode) && (
          <BridgeTargetRings
            unit={selectedUnitNode}
            tiles={tiles}
            biomeMap={biomeMap}
            onBuild={onBuildBridge}
          />
        )}
        {selectedTileData && (
          <SelectionRing
            position={selectedTileData.position}
            surfaceY={tileElevationAt(selectedTileData.q, selectedTileData.r, biomeMap)}
          />
        )}
        {showTerritory && (
          <TerritoryOverlay tiles={tiles} visibleCoords={visibleCoords} biomeMap={biomeMap} />
        )}
        {particleBounds && (
          <AtmosphericClouds bounds={particleBounds} climate={climate} enabled={showClouds} />
        )}
        {particleBounds && <AmbientParticles bounds={particleBounds} count={200} day={seasonDay} />}
      </group>
      <PostProcessing hasSelection={!!selectedUnitId} doctrine={doctrine} />
      <CinematicCameraRig controls={controlsRef} />
      <CameraFlight controls={controlsRef} request={focusRequest} />
      <CameraViewPublisher controls={controlsRef} />
      <CameraPanInput controls={controlsRef} />
      <CameraPanClamp tiles={tiles} seaRings={seaRings} scale={boardScale} controls={controlsRef} />
      {new URLSearchParams(window.location.search).get('action') === '1' && (
        <ActionMode
          enabled
          controls={controlsRef}
          tiles={tiles}
          onExit={() => {
            const url = new URL(window.location.href);
            url.searchParams.delete('action');
            window.history.replaceState(null, '', url.toString());
          }}
        />
      )}
      <OrbitControls
        ref={controlsRef as never}
        enableDamping
        maxPolarAngle={Math.PI / 2.05}
        minDistance={6 * boardScale}
        maxDistance={60 * boardScale}
      />
    </Canvas>
  );
}
